import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import { leadKeys } from '../leads/queries';
import * as api from './api';
import type { BidInput } from './types';

export const bidKeys = {
  all: ['bids'] as const,
  lists: () => [...bidKeys.all, 'list'] as const,
  list: (params: api.BidListParams) => [...bidKeys.lists(), params] as const,
  detail: (id: string | number) => [...bidKeys.all, 'detail', id] as const,
  rollups: () => [...bidKeys.all, 'rollup'] as const,
  rollup: (params: api.BidRollupParams) => [...bidKeys.rollups(), params] as const,
};

export function useBids(params: api.BidListParams) {
  return useQuery({
    queryKey: bidKeys.list(params),
    queryFn: () => api.listBids(params),
    placeholderData: keepPreviousData,
  });
}

export function useBid(id: string | number) {
  return useQuery({
    queryKey: bidKeys.detail(id),
    queryFn: () => api.getBid(id),
    enabled: Boolean(id),
  });
}

export function useBidRollup(params: api.BidRollupParams) {
  return useQuery({
    queryKey: bidKeys.rollup(params),
    queryFn: () => api.getBidRollup(params),
    placeholderData: keepPreviousData,
  });
}

function invalidateBidReads(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: bidKeys.lists() });
  qc.invalidateQueries({ queryKey: bidKeys.rollups() });
}

export function useCreateBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BidInput) => api.createBid(input),
    onSuccess: () => {
      invalidateBidReads(qc);
      toast.success('Bid created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateBid(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<BidInput>) => api.updateBid(id, input),
    onSuccess: (bid) => {
      invalidateBidReads(qc);
      qc.setQueryData(bidKeys.detail(id), bid);
      toast.success('Bid updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useArchiveBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.archiveBid(id),
    onSuccess: (_void, id) => {
      invalidateBidReads(qc);
      qc.invalidateQueries({ queryKey: bidKeys.detail(id) });
      toast.success('Bid archived');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRestoreBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.restoreBid(id),
    onSuccess: (bid) => {
      invalidateBidReads(qc);
      qc.setQueryData(bidKeys.detail(bid.public_id), bid);
      toast.success('Bid restored');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function usePurgeBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.purgeBid(id),
    onSuccess: (_void, id) => {
      invalidateBidReads(qc);
      qc.removeQueries({ queryKey: bidKeys.detail(id) });
      toast.success('Bid permanently deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/** Convert a bid to a Lead (once). Also refreshes Leads, since one was created. */
export function useConvertBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.convertBid(id),
    onSuccess: (_lead, id) => {
      invalidateBidReads(qc);
      qc.invalidateQueries({ queryKey: bidKeys.detail(id) });
      qc.invalidateQueries({ queryKey: leadKeys.lists() });
      toast.success('Bid converted to a lead');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
